// df-846bfc537888: opening the shared `global_common` database is retryable now, and a retry has
// to REBUILD.
//
// `CommonDbService.initializeAsync` is wrapped by `generateAsyncInitShutdownPair`, which no longer
// remembers a failed attempt — so a second call runs a real second attempt. That only helps if the
// service is retry-safe, and it was not: it assigned the new wrapper to `this.database` BEFORE
// awaiting the open. A failure then left a half-built wrapper published, and the retry took the
// warm-resume branch and connected only the remote against a local store that had never been
// opened — reporting success on a wrong state, which is worse than the failure it replaced.
//
// The ordering is the whole fix, and getting it wrong again would be silent. So the decision lives
// here as a pure, node-loadable helper and the service supplies the real work and the side effects.
// (`CommonDbService` pulls in Angular and PouchDB and cannot load under node — same reason, and
// same shape, as `config-db-init.ts`.) Generic over the wrapper type so this module imports none of
// that.
//
// df-54ca73a44654: holding a wrapper is not the same as holding a LIVE one. Hence
// `isDatabaseUsable` — the warm-resume branch asks whether the wrapper can still be resumed onto,
// not merely whether there is one. Why that matters, and why the field is not simply cleared on
// shutdown instead: `design-docs/warm-resume.md`, "The Phase A fallback runs in the SAME page".

export interface CommonDbOpenDeps<TDatabase> {
  /** The wrapper the service already holds, if any. A warm resume opens the local store outside
   *  this flow, and then only the remote connection is missing. */
  existingDatabase: TDatabase | null | undefined;
  /** Whether the wrapper the service already holds can still be used. False for one that has been
   *  shut down: its local store is closed and resuming onto it would build a remote connection
   *  against nothing. Such a wrapper is rebuilt from scratch instead. */
  isDatabaseUsable: (database: TDatabase) => boolean;
  /** Connect the remote and start sync for a database whose local store is already open. */
  connectRemoteAsync: (database: TDatabase) => Promise<void>;
  /** Make a fresh wrapper. It is registered with the provider the moment it is made, which is why
   *  a discarded one has to be retired rather than simply dropped. */
  createDatabase: () => TDatabase;
  /** Open a fresh wrapper's local store and start its sync. */
  openDatabaseAsync: (database: TDatabase) => Promise<void>;
  /** Retire a wrapper whose open failed. Must not throw and must not block — it runs while the
   *  caller's own error is still on its way up. */
  retireFailedDatabase: (database: TDatabase) => void;
  /** Publish a successfully opened wrapper on the service. */
  publishDatabase: (database: TDatabase) => void;
}

export async function openCommonDbAsync<TDatabase>(deps: CommonDbOpenDeps<TDatabase>): Promise<void> {
  // if the local store is already open (warm resume), only the remote connection is missing. A
  // wrapper that has been shut down does NOT count as open — it falls through and is rebuilt.
  if (deps.existingDatabase && deps.isDatabaseUsable(deps.existingDatabase)) {
    await deps.connectRemoteAsync(deps.existingDatabase);
    return;
  }

  const database = deps.createDatabase();
  try {
    await deps.openDatabaseAsync(database);
  } catch (error) {
    // if the open failed, retire the half-built wrapper and publish NOTHING, so a retry rebuilds
    // from scratch instead of finding a published wrapper and taking the warm-resume branch above
    deps.retireFailedDatabase(database);
    throw error;
  }
  deps.publishDatabase(database);
}
